import { createSelector } from 'reselect'
import {
  currentPathSelector,
  fetchingStatusesSelector,
  isAuthenticatedSelector,
  isBoostrappedSelector,
  selectCurrentFolderId,
  selectDeviceTypeIsDesktop,
} from 'selectors/app'
import {
  selectCurrentUser,
  selectPreferences,
  selectPrefersUnifiedInbox,
} from 'ducks/currentUser/selectors'
import { selectMailboxes, selectMailboxesById } from 'selectors/mailboxes_base'
import { selectCurrentMailboxId } from 'selectors/mailboxes'
import {
  selectTicketSearchOperatorValueMap,
  selectTicketSearchResultsByQueryId,
} from 'selectors/search/base'
import {
  selectTicketSorting,
  selectPreviousTicketSorting,
} from 'selectors/sorting'

import { any, getLength } from 'util/arrays'
import { isMe } from 'util/folders'
import { mapObject } from 'util/objects'
import { downcase } from 'util/strings'
import {
  constructSearchQueryObject,
  constructSearchQueryId,
  constructSearchQueryString,
  constructEncodedSearchQueryString,
} from 'util/search'

import {
  foldersByIdSelector,
  selectOrderedFolderIds,
  selectFolders,
} from './folders_base'

export { foldersByIdSelector, selectOrderedFolderIds, selectFolders }

const getFolderName = (byId, folderId) => {
  if (!folderId) return null
  const found = byId[folderId]
  if (!found) return null
  return found.name
}

export const selectCurrentFolderName = createSelector(
  foldersByIdSelector,
  selectCurrentFolderId,
  (byId, folderId) => getFolderName(byId, folderId)
)

export const selectCurrentFolder = createSelector(
  foldersByIdSelector,
  selectCurrentFolderId,
  (byId, folderId) => {
    if (!folderId) return null
    return byId[folderId] || null
  }
)

export const selectCurrentFolderCount = createSelector(
  selectCurrentFolder,
  selectCurrentMailboxId,
  (folder, mailboxId) => {
    return folder && folder.counts && folder.counts[mailboxId || 'total']
  }
)

const folderMailboxTicketIds = (folder, mailboxId) => {
  if (!folder) return null
  if (!folder.mailboxTickets) return null
  if (folder.mailboxTickets[mailboxId]) return folder.mailboxTickets[mailboxId]
  return null
}

export const selectCurrentFolderMailboxTickets = createSelector(
  selectCurrentFolder,
  selectCurrentMailboxId,
  (folder, mailboxId) => folderMailboxTicketIds(folder, mailboxId)
)

export const selectCurrentFolderIfTickets = createSelector(
  selectCurrentFolder,
  selectCurrentFolderMailboxTickets,
  (folder, hasTickets) => (hasTickets ? folder : null)
)

export const selectCurrentFolderFailedToLoad = createSelector(
  selectCurrentFolderIfTickets,
  folder => (folder ? folder.failed : false)
)

export const shouldFetchCurrentFolderSelector = createSelector(
  isAuthenticatedSelector,
  isBoostrappedSelector,
  selectCurrentFolderIfTickets,
  fetchingStatusesSelector,
  currentPathSelector,
  selectTicketSorting,
  selectPreviousTicketSorting,
  (auth, isBootstrapped, current, statuses, path, sortBy, previousSortBy) => {
    if (!auth) return false
    if (!isBootstrapped) return false // wait til app loaded
    // if we're currently fetching, we should never trigger another fetch
    if (statuses.fetchCurrentFolderWithTickets) return false
    if (path === '/') return false // handled by starred search instead
    const onRightPage = path.match('folders/') || path.match('mailbox/')
    const should = !!(onRightPage && !current)
    const sortingChanged = sortBy !== previousSortBy
    return should || sortingChanged
  }
)

export const shouldFetchPreviousFolderForDesktop = createSelector(
  isAuthenticatedSelector,
  isBoostrappedSelector,
  selectDeviceTypeIsDesktop,
  selectCurrentFolderIfTickets,
  fetchingStatusesSelector,
  currentPathSelector,
  selectTicketSorting,
  selectPreviousTicketSorting,
  (
    auth,
    isBootstrapped,
    isDesktop,
    currentFolder,
    statuses,
    path,
    sortBy,
    previousSortBy
  ) => {
    if (!isDesktop) return false
    if (!auth) return false
    if (!isBootstrapped) return false
    // if we're currently fetching, we should never trigger another fetch
    if (statuses.fetchCurrentFolderWithTickets) return false
    const onRightPage = path.match('tickets/')
    const should =
      !!(onRightPage && !currentFolder) || sortBy !== previousSortBy
    return should
  }
)

function buildSearchObject(folder, mailbox) {
  const searchObject = {}
  searchObject.folder = [folder.id]
  if (mailbox) {
    const mailboxName = downcase(mailbox.name)
    searchObject.mailbox = [mailboxName]
  }
  return searchObject
}

function queryFor(valueMap, folder, mailbox) {
  const searchObject = buildSearchObject(folder, mailbox)
  return constructSearchQueryObject(searchObject, valueMap)
}

function pathFor(searchObject) {
  const string = constructEncodedSearchQueryString(searchObject)
  return `/search/${string}`
}

function decorate(folder, mailbox, valueMap, searchesById) {
  const searchObject = buildSearchObject(folder, mailbox)
  const searchQueryId = constructSearchQueryId(searchObject, valueMap)
  const search = searchesById[searchQueryId]
  const count = search ? search.totalCount : 0
  const string = constructSearchQueryString(searchObject)
  return {
    ...folder,
    path: pathFor(searchObject),
    string,
    count,
  }
}

export const selectAllFolderQueries = createSelector(
  selectMailboxes,
  selectFolders,
  foldersByIdSelector,
  selectTicketSearchOperatorValueMap,
  selectPrefersUnifiedInbox,
  (mailboxes, folders, foldersById, valueMap, prefersUnified) => {
    const result = []
    folders.forEach(folder => {
      result.push(queryFor(valueMap, folder))
    })
    if (!prefersUnified)
      mailboxes.forEach(mailbox => {
        mailbox.folders.forEach(folderId => {
          const folder = foldersById[folderId]
          if (folder) {
            result.push(queryFor(valueMap, folder, mailbox))
          }
        })
      })
    return result
  }
)

export const selectMailboxFoldersById = createSelector(
  foldersByIdSelector,
  selectMailboxesById,
  selectOrderedFolderIds,
  selectPreferences,
  selectTicketSearchOperatorValueMap,
  selectTicketSearchResultsByQueryId,
  (byId, mailboxes, folderIds, preferences, valueMap, searchesById) => {
    const r = {
      ...mapObject(mailbox => {
        return mailbox.folders.map(folderId => {
          return decorate(byId[folderId], mailbox, valueMap, searchesById)
        })
      })(mailboxes),
      [null]: folderIds.map(folderId => {
        return decorate(byId[folderId], null, valueMap, searchesById)
      }),
    }
    return r
  }
)

export const selectCurrentMailboxFolders = createSelector(
  selectMailboxFoldersById,
  selectCurrentMailboxId,
  (mailboxFolders, mailboxId) => {
    return mailboxFolders[mailboxId].filter(e => !!e)
  }
)

export const selectCurrentMailboxHasFolders = createSelector(
  selectCurrentMailboxFolders,
  folders => folders && getLength(folders) > 0
)

export const selectCurrentMailboxFolders2 = () => [
  { name: 'Spam', count: 10 },
  { name: 'Spam', count: 10 },
  { name: 'Spam', count: 10 },
  { name: 'Spam', count: 10 },
  { name: 'Spam', count: 10 },
]

export const selectCurrentFolderIndex = createSelector(
  selectCurrentMailboxFolders,
  selectCurrentFolderId,
  (folders, folderId) => {
    if (!folders || !folderId) return null
    return folders.map(f => f.id).indexOf(folderId)
  }
)

const isDraftConditionFor = id => c => {
  return c.param === 'drafts' && (isMe(c.value) || c.value === id)
}

export const selectFolderIdsMatchingDraftForCurrentUser = createSelector(
  foldersByIdSelector,
  selectCurrentUser,
  (folders, user) => {
    return Object.keys(folders).filter(id => {
      const folder = folders[id]
      if (!folder || !folder.conditions) return false
      return any(isDraftConditionFor(user.id), folder.conditions)
    })
  }
)
